// src/store/features/vinaSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getNewObjWithUpdate } from "src/utils/kinaseUserUtils";


interface ValidationMap {
  [key: string]: boolean;
}

interface VinaParams {
  [key: string]: any;
}

interface FrameData {
  score: number;
  rmsd1: number;
  rmsd2: number;
  frame: string;
}

export interface iKinaseUserVinaState {
  vinaParams: VinaParams;
  validation: ValidationMap;
  hideDockingBoxParams: boolean;
  tabIdx: number;
  receptorFileName: string;
  ligandFileName: string;
  receptorContents: string;
  ligandContents: string;
  crystalContents: string;
  outputContents: string;
  dockedContents: string;
  pdbOutputFrames: FrameData[];
  stdOut: string;
  convertFileModalShow: boolean;
  convertFileExt: string;
  convertFileType: string;
  convertFile: any;
  receptorForceValidate: boolean;
  ligandForceValidate: boolean;
  drawSmilesModalShow: boolean;
  modalShow: boolean;
  modalTitle: string;
  modalBody: string;
  vinaParamsValidates: boolean;
  time: number;
  showKeepProteinOnlyLink: boolean;
  extractedMoleculeData: {
    chains: any[];
    ligands: any[];
  };
  isManualMode: boolean; // 수동 모드 플래그 추가
}

const initialState: iKinaseUserVinaState = {
  vinaParams: {},
  validation: {},
  hideDockingBoxParams: false,
  tabIdx: 0,
  receptorFileName: "",
  ligandFileName: "",
  receptorContents: "",
  ligandContents: "",
  crystalContents: "",
  outputContents: "",
  dockedContents: "",
  pdbOutputFrames: [],
  stdOut: "",
  convertFileModalShow: false,
  convertFileExt: "PDB",
  convertFileType: "receptor",
  convertFile: null,
  receptorForceValidate: false,
  ligandForceValidate: false,
  drawSmilesModalShow: false,
  modalShow: false,
  modalTitle: "Title",
  modalBody: "Some text here...",
  vinaParamsValidates: false,
  time: 0,
  showKeepProteinOnlyLink: true,
  extractedMoleculeData: {
    chains: [],
    ligands: []
  },
  isManualMode: false, //기본값은 자동 모드
};

export const kinaseUservinaSlice = createSlice({
  name: "kinaseUserVina",
  initialState,
  reducers: {
    setVar(state, action: PayloadAction<{ name: string; val: any }>) {
      (state as any)[action.payload.name] = action.payload.val;
    },
    setVinaParam(state, action: PayloadAction<{ name: string; val: any }>) {
      state.vinaParams = getNewObjWithUpdate(
        state.vinaParams,
        action.payload.name,
        action.payload.val
      );
      state.hideDockingBoxParams = state.vinaParams["score_only"] === true;
    },
    setValidationParam(
      state,
      action: PayloadAction<{ name: string; val: boolean }>
    ) {
      state.validation = getNewObjWithUpdate(
        state.validation,
        action.payload.name,
        action.payload.val
      );
    },
    setReceptorContents(state, action: PayloadAction<string | null>) {
      state.receptorContents = action.payload || "";
      // 새 파일이 로드되면 자동 모드로 리셋
      state.isManualMode = false;
    },
    setLigandContents(state, action: PayloadAction<string | null>) {
      state.ligandContents = action.payload || "";
    },
    setExtractedMoleculeData(state, action: PayloadAction<{ chains: any[], ligands: any[] }>) {
      state.extractedMoleculeData = action.payload;
    },
    // 수동 모드 설정 액션 추가
    setManualMode(state, action: PayloadAction<boolean>) {
      state.isManualMode = action.payload;
    },
    disableTabs(state, action: PayloadAction<{ [key: string]: boolean }>) {
      Object.entries(action.payload).forEach(([key, val]) => {
        (state as any)[key] = val;
      });
    },
    updateFileName(
      state,
      action: PayloadAction<{ type: string; filename: string }>
    ) {
      (state as any)[`${action.payload.type}FileName`] =
        action.payload.filename;
    },
    openModal(state, action: PayloadAction<{ title: string; body: string }>) {
      state.modalShow = true;
      state.modalTitle = action.payload.title;
      state.modalBody = action.payload.body;
    },
    openConvertFileModal(
      state,
      action: PayloadAction<{ ext: string; type: string; file: any }>
    ) {
      state.convertFileModalShow = true;
      state.convertFileExt = action.payload.ext;
      state.convertFileType = action.payload.type;
      state.convertFile = action.payload.file;
    },
    drawSmilesModal(state) {
      state.drawSmilesModalShow = true;
    },
    outputToData(state, action: PayloadAction<{ outPdbqtFileTxt: string }>) {
      const { outPdbqtFileTxt } = action.payload;
      const re =
        /^REMARK VINA RESULT:\W+?([0-9.-]+)\W+?([0-9.-]+)\W+?([0-9.-]+)$/gm;
      const matches: FrameData[] = [];

      let match;
      let i = 1;
      while ((match = re.exec(outPdbqtFileTxt)) !== null) {
        matches.push({
          score: +match[1],
          rmsd1: +match[2],
          rmsd2: +match[3],
          frame: "", // populated later
        });
        i++;
      }

      const framesPDBQTs = outPdbqtFileTxt.split("ENDMDL");
      const framePDBs = framesPDBQTs
        .map((t, idx) => ({ ...matches[idx], frame: t }))
        .filter((t) => t.frame !== "");

      state.pdbOutputFrames = framePDBs;
      state.dockedContents = framePDBs[0]?.frame || "";
    },
  },
});

export const {
  setVar,
  setVinaParam,
  setValidationParam,
  setReceptorContents,
  setLigandContents,
  setExtractedMoleculeData,
  setManualMode,
  disableTabs,
  updateFileName,
  openModal,
  openConvertFileModal,
  drawSmilesModal,
  outputToData,
} = kinaseUservinaSlice.actions;

export default kinaseUservinaSlice.reducer;